import { toast } from 'sonner'
import { GENERIC_REFUSAL_KEY, logRefusal, refusalKey } from '@/lib/api/refusal-key'
import { deleteRefusalReason, erDeleteBlockedKey } from '@/lib/er/delete-gate'

/**
 * What the Email Remediator's triage actions tell an admin when the API refuses
 * one, or skips one, as catalogue keys (CLAUDE.md rule 9: never the API's
 * English `message`, never a bare reason code).
 */

/**
 * The refusals the triage screen words: the API's ER_REMEDIATION_ERROR_CODES
 * (apps/api/src/modules/er/er.constants.ts) and ER_DOMAIN_ERROR_CODES
 * (apps/api/src/modules/er/services/er-domain-ownership.ts). A missing and a
 * stale confirmed count read the same sentence: either way the list has to be
 * reloaded. A spec reads both API constants, so a new code cannot be left out.
 */
export const ER_TRIAGE_REFUSAL_KEYS: ReadonlyMap<string, string> = new Map([
  // A permanent Delete asked of a place that never deletes (the org-wide trigger, Threat Logs, update-status).
  ['er_delete_not_available_here', 'er.triage.refused.deleteNotAvailableHere'],
  // A Delete that is missing a permission (er_delete_permission_missing, apps/api/.../er-delete-gate.ts) is
  // worded by its `reason` (role / switch / provider scope), see erTriageRefusalKey; this entry is the sentence
  // for a reason this screen does not know. `er_hard_delete_disabled` is the older code for the switch alone.
  ['er_delete_permission_missing', 'er.delete.blocked.generic'],
  ['er_hard_delete_disabled', 'er.triage.refused.hardDeleteDisabled'],
  ['er_blast_radius_exceeded', 'er.triage.refused.blastRadiusExceeded'],
  ['er_count_required', 'er.triage.refused.countMismatch'],
  ['er_count_mismatch', 'er.triage.refused.countMismatch'],
  ['er_no_accessible_domains', 'er.triage.refused.noAccessibleDomains'],
  ['er_nothing_to_match', 'er.triage.refused.nothingToMatch'],
  ['er_no_reporter', 'er.triage.refused.noReporter'],
  // ER_DOMAIN_ERROR_CODES (er-domain-ownership.ts): the domain checks of ErDomainGuard and of the
  // catalogue writes. Under "All domains" an action needs a domain, and a ticket of another domain
  // is not acted on through a mismatching header.
  ['er_domain_required', 'er.triage.refused.domainRequired'],
  ['er_ticket_domain_mismatch', 'er.triage.refused.ticketDomainMismatch'],
  ['er_domain_not_in_workspace', 'er.triage.refused.domainNotInWorkspace'],
])

/**
 * The key for a refused triage action: its errorCode's sentence, else the HTTP
 * status's, else (a server fault, a lost connection) the action's own
 * "could not" sentence, which says what did not happen.
 */
export function erTriageRefusalKey(error: unknown, failedKey: string): string {
  // "Can't be done: permission is not there" names WHICH permission (owner, 27 Oct 2026).
  const missing = deleteRefusalReason(error)
  if (missing) return erDeleteBlockedKey(missing)
  const key = refusalKey(error, ER_TRIAGE_REFUSAL_KEYS)
  return key === GENERIC_REFUSAL_KEY ? failedKey : key
}

/**
 * The `onError` of a triage write: the admin reads erTriageRefusalKey's
 * sentence, and what the API said goes to the console (logRefusal) under
 * `what`, for support.
 */
export function refuse(t: (key: string) => string, what: string, failedKey: string) {
  return (err: unknown) => {
    logRefusal(what, err)
    toast.error(t(erTriageRefusalKey(err, failedKey)))
  }
}

/**
 * Why a SOC forward, SOC notice or report to Microsoft was skipped: the API's
 * `skippedReason` codes (graph-forward / er-notify-soc / er-report-to-microsoft
 * services). Each key is a clause that completes the action's own sentence.
 */
const SKIP_REASONS = new Set([
  'no_domain',
  'not_graph',
  'unsupported_provider',
  'integration_inactive',
  'no_soc_address',
  'no_soc_recipients',
  'no_target_address',
  'no_recipient',
  'mailbox_unconfined',
  'no_provider_message',
  'source_copy_gone',
  'send_failed',
  'forward_failed',
])

/** The clause for a skipped reason; one the screen does not know reads a plain clause, never the code. */
export function erSkipReasonKey(reason: string | null | undefined): string {
  return reason && SKIP_REASONS.has(reason) ? `er.triage.skipReason.${reason}` : 'er.triage.skipReason.unknown'
}
